import {
  AnalysisDetails,
  type AnalysisTab,
} from '@/features/analyses/components';
import { CapitalGlobalAnalysisPage } from '@/features/analyses/capital-global';
import { PmeAnalysisPage } from '@/features/analyses/pme';
import {
  CAPITAL_GLOBAL_PRODUCT_CODE,
  GROUP_LIFE_PRODUCT_CODE,
} from '@/app/config/products.config';
import { useProposalDetails } from '@/features/quotes/hooks/homeowner/use-proposal-details.hook';
import { requireInsurerUnderwritingAccess, requirePermissions } from '@/shared/utils';
import { Spinner } from '@noorden-insurtech/alba-design-system';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';

const analysisSearchSchema = z.object({
  tab: z
    .enum(['insurance', 'risk', 'commercialization', 'calculation'])
    .optional(),
});

export const Route = createFileRoute('/_app/analyses/$policyId/')({
  component: RouteComponent,
  validateSearch: analysisSearchSchema,
  beforeLoad: async () => {
    const [hasPermission, hasUnderwritingAccess] = await Promise.all([
      requirePermissions(['policies.quotes.read']),
      requireInsurerUnderwritingAccess(),
    ]);
    if (!hasPermission || !hasUnderwritingAccess) {
      throw redirect({ to: '/dashboard' });
    }
  },
});

function RouteComponent() {
  const { policyId } = Route.useParams();
  const { tab } = Route.useSearch();

  return (
    <AnalysisRouteResolver
      policyId={policyId}
      initialTab={(tab as AnalysisTab) ?? 'insurance'}
    />
  );
}

type ResolverProps = {
  policyId: string;
  initialTab: AnalysisTab;
};

function AnalysisRouteResolver({ policyId, initialTab }: ResolverProps) {
  const { data: proposal, isLoading } = useProposalDetails(policyId);

  if (isLoading) {
    return (
      <div className='flex justify-center py-24'>
        <Spinner size='lg' colorScheme='blue' />
      </div>
    );
  }

  if (proposal?.productCode === GROUP_LIFE_PRODUCT_CODE) {
    return <PmeAnalysisPage policyId={policyId} />;
  }

  if (proposal?.productCode === CAPITAL_GLOBAL_PRODUCT_CODE) {
    return <CapitalGlobalAnalysisPage policyId={policyId} />;
  }

  return (
    <AnalysisDetails policyId={policyId} initialTab={initialTab} />
  );
}
